import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Pill, FlaskConical, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Pharmaceutical = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/pharmaceutical"]?.title} description={seoData["industries/pharmaceutical"]?.description} path="/industries/pharmaceutical" keywords={seoData["industries/pharmaceutical"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Pharmaceutical"
        description="Coating and repair contractor for pharmaceutical manufacturing. Chemical-resistant floor coatings, containment systems, clean room equipment repair, and GMP-compliant surface finishes."
        backgroundKey="pharmaceutical"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Solvent and chemical spill damage on production floors',
              'Process vessel and reactor corrosion',
              'Containment area failures at API and chemical storage',
              'Clean room equipment corrosion and contamination',
              'Regulatory compliance for GMP manufacturing areas',
              'Pipe corrosion from high-purity and WFI water systems'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-teal-50 rounded-lg">
                <Pill className="text-teal-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  GMP Floor Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Seamless, chemical-resistant floor coatings for pharmaceutical manufacturing areas, labs, and warehouses. Epoxy and polyurethane systems that meet cGMP requirements for cleanability and chemical resistance.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Chemical Containment Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Secondary containment systems for API storage, solvent tanks, and chemical feed areas. Chemical-resistant bund lining and spill prevention coatings meeting EPA and state pharmaceutical regulations.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Process Equipment Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Reactor, vessel, and heat exchanger repair using chemical-resistant epoxy systems. Restore process equipment without replacement or lengthy shutdowns.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Utility System Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pipe coating and repair for WFI, purified water, and process utility systems. Protective coatings for HVAC systems, cooling towers, and utility infrastructure.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Pharmaceutical?</h2>
              <div className="space-y-3">
                {['cGMP compliant coating systems and application methods',
                  'Chemical-resistant surfaces for solvent and API environments',
                  'Low-odor and low-VOC systems for occupied facilities',
                  'Validated cleaning and documentation procedures',
                  'Minimal disruption to production schedules',
                  'Full QA/QC documentation for regulatory compliance'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-teal-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Pharmaceutical Compliance</h3>
              <p className="text-teal-100 mb-6">BCS understands the stringent requirements of pharmaceutical manufacturing. Our coating systems and application procedures are designed to meet cGMP and regulatory standards.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View Protective Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Pharmaceutical Facility</h2>
          <p className="text-blue-100 mb-8">Contact us for a coating assessment for your pharmaceutical manufacturing environment.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pharmaceutical;
